import styled from 'styled-components';
import { useParams, Link, Navigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiCheck, mdiOpenInNew } from '@mdi/js';
import { getClientById, clients } from '../../data/clients';
import { Section, SectionHeader } from '../ui/Section';
import { GlassCard } from '../ui/GlassCard';
import { TechStackBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Container } from '../ui/Container';
import { CTASection } from '../sections/CTASection';
import { PageHead } from '../seo';

const PageWrapper = styled.div`
  padding-top: 73px;
`;

const HeroSection = styled.section<{ $color: string }>`
  padding: var(--space-16) 0;
  background: linear-gradient(
    135deg,
    ${props => props.$color}15 0%,
    transparent 50%
  );
  position: relative;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  margin-bottom: var(--space-6);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--color-text-primary);
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroText = styled.div``;

const CategoryBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  margin-bottom: var(--space-4);
`;

const ClientTitle = styled.h1`
  font-size: var(--text-5xl);
  margin-bottom: var(--space-2);
`;

const ClientTagline = styled.p`
  font-size: var(--text-xl);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-6);
`;

const ClientDescription = styled.p`
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-8);
`;

const CTAButtons = styled.div`
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const HeroVisual = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  @media (max-width: 968px) {
    order: -1;
  }
`;

const MockupWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
`;

const LaptopImage = styled.img`
  width: 100%;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.2));
`;

const PhoneImage = styled.img`
  position: absolute;
  right: -10%;
  bottom: -10%;
  width: 35%;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.25));
`;

const PlaceholderVisual = styled.div<{ $color: string }>`
  width: 300px;
  height: 200px;
  border-radius: var(--radius-2xl);
  background: linear-gradient(
    145deg,
    ${props => props.$color}30,
    ${props => props.$color}10
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 80px;
  box-shadow: var(--shadow-xl);
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
`;

const ServiceCard = styled(GlassCard)`
  text-align: center;
`;

const ServiceIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: var(--color-success);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-3);
`;

const ServiceName = styled.h3`
  font-size: var(--text-base);
`;

const ResultsSection = styled(Section)`
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-6);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ResultCard = styled(GlassCard)`
  text-align: center;
`;

const ResultValue = styled.div`
  font-size: var(--text-4xl);
  font-weight: var(--font-bold);
  color: var(--color-success);
  margin-bottom: var(--space-2);
`;

const ResultLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

const TestimonialCard = styled(GlassCard)`
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
`;

const TestimonialQuote = styled.blockquote`
  font-size: var(--text-xl);
  font-style: italic;
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-6);
  color: var(--color-text-primary);

  &::before {
    content: '"';
    font-size: var(--text-4xl);
    color: var(--color-brand-primary);
    line-height: 0;
    vertical-align: -0.3em;
    margin-right: var(--space-2);
  }
`;

const TestimonialAuthor = styled.div`
  font-weight: var(--font-semibold);
`;

const TestimonialRole = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const TechSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  justify-content: center;
`;

const OtherClients = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OtherClientCard = styled(Link)<{ $color: string }>`
  display: flex;
  flex-direction: column;
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  background: var(--glass-background);
  border: 1px solid var(--glass-border);
  text-decoration: none;
  transition: all var(--transition-base);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: ${props => props.$color};
  }
`;

const OtherClientName = styled.div`
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--space-1);
`;

const OtherClientTagline = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const categoryColors: Record<string, string> = {
  'ecommerce': '#7AB55C',
  'corporate': '#007aff',
  'webapp': '#511461',
  'mobile': '#ff9500',
};

const categoryLabels: Record<string, string> = {
  'ecommerce': 'E-Commerce',
  'corporate': 'Corporate',
  'webapp': 'Web App',
  'mobile': 'Mobile',
};

function parseResult(result: string): { value: string; label: string } {
  const match = result.match(/^([0-9+%]+)\s*(.*)$/);
  if (match) {
    return { value: match[1], label: match[2] };
  }
  const parts = result.split(' ');
  return { value: parts[0], label: parts.slice(1).join(' ') };
}

export function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const client = clientId ? getClientById(clientId) : undefined;

  if (!client) {
    return <Navigate to="/portfolio" replace />;
  }

  const otherClients = clients.filter(c => c.id !== client.id).slice(0, 3);
  const ogImage = client.imageLaptop
    ? `https://portfolio.data26.solutions${client.imageLaptop}`
    : undefined;

  return (
    <PageWrapper>
      <PageHead
        title={`${client.name} — ${client.tagline}`}
        description={client.description}
        ogImage={ogImage}
      />
      <HeroSection $color={client.accentColor}>
        <Container>
          <BackLink to="/portfolio">
            <Icon path={mdiArrowLeft} size={0.8} />
            Back to Portfolio
          </BackLink>

          <HeroContent>
            <HeroText>
              <CategoryBadge $color={categoryColors[client.category]}>
                {categoryLabels[client.category]}
              </CategoryBadge>
              <ClientTitle>{client.name}</ClientTitle>
              <ClientTagline>{client.tagline}</ClientTagline>
              <ClientDescription>{client.description}</ClientDescription>

              <CTAButtons>
                {client.links.live && (
                  <Button as="a" href={client.links.live} target="_blank" rel="noopener noreferrer">
                    <Icon path={mdiOpenInNew} size={0.9} />
                    Visit Live Site
                  </Button>
                )}
                <Button as={Link} to="/contact" variant="secondary">
                  Start Your Project
                </Button>
              </CTAButtons>
            </HeroText>

            <HeroVisual>
              {client.imageLaptop ? (
                <MockupWrapper>
                  <LaptopImage src={client.imageLaptop} alt={`${client.name} on laptop`} />
                  {client.imagePhone && (
                    <PhoneImage src={client.imagePhone} alt={`${client.name} on mobile`} />
                  )}
                </MockupWrapper>
              ) : (
                <PlaceholderVisual $color={client.accentColor}>
                  {client.name.charAt(0)}
                </PlaceholderVisual>
              )}
            </HeroVisual>
          </HeroContent>
        </Container>
      </HeroSection>

      <Section>
        <SectionHeader
          title="Services Provided"
          subtitle={`What we delivered for ${client.name}.`}
          centered
        />
        <ServicesGrid>
          {client.services.map((service, index) => (
            <ServiceCard key={index}>
              <ServiceIcon>
                <Icon path={mdiCheck} size={1} />
              </ServiceIcon>
              <ServiceName>{service}</ServiceName>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Section>

      {client.results && client.results.length > 0 && (
        <ResultsSection background="secondary">
          <SectionHeader
            title="Results"
            subtitle="The impact of our work."
            centered
          />
          <ResultsGrid>
            {client.results.map((result, index) => {
              const parsed = parseResult(result);
              return (
                <ResultCard key={index}>
                  <ResultValue>{parsed.value}</ResultValue>
                  <ResultLabel>{parsed.label}</ResultLabel>
                </ResultCard>
              );
            })}
          </ResultsGrid>
        </ResultsSection>
      )}

      {client.testimonial && (
        <Section>
          <SectionHeader
            title="Client Testimonial"
            subtitle="In their words."
            centered
          />
          <TestimonialCard>
            <TestimonialQuote>{client.testimonial.quote}</TestimonialQuote>
            <TestimonialAuthor>{client.testimonial.author}</TestimonialAuthor>
            <TestimonialRole>{client.testimonial.role}</TestimonialRole>
          </TestimonialCard>
        </Section>
      )}

      <Section background="secondary">
        <SectionHeader
          title="Technology Stack"
          subtitle="The tools we used to bring this project to life."
          centered
        />
        <TechSection>
          {client.techStack.map(tech => (
            <TechStackBadge key={tech} tech={tech} size="md" />
          ))}
        </TechSection>
      </Section>

      <Section>
        <SectionHeader
          title="More Projects"
          subtitle="Explore other work we've done."
          centered
        />
        <OtherClients>
          {otherClients.map(otherClient => (
            <OtherClientCard key={otherClient.id} to={`/portfolio/${otherClient.id}`} $color={otherClient.accentColor}>
              <OtherClientName>{otherClient.name}</OtherClientName>
              <OtherClientTagline>{otherClient.tagline}</OtherClientTagline>
            </OtherClientCard>
          ))}
        </OtherClients>
      </Section>

      <CTASection
        title="Ready to Start Your Project?"
        subtitle="Let's create something amazing together."
        primaryCTA={{ label: 'Get in Touch', to: '/contact' }}
        secondaryCTA={{ label: 'View All Projects', to: '/portfolio' }}
      />
    </PageWrapper>
  );
}
